import ExcelJS from "exceljs";
import { convertTime } from "../../../helpers/helpers";
import i18n from "../../../i18n";

const BORDER = {
  top: { style: "thin", color: { argb: "000000" } },
  left: { style: "thin", color: { argb: "000000" } },
  bottom: { style: "thin", color: { argb: "000000" } },
  right: { style: "thin", color: { argb: "000000" } },
};

const HEADER_STYLE = {
  font: {
    bold: true,
    color: { argb: "FFFFFF" },
  },
  fill: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "3498DB" },
  },
  alignment: {
    horizontal: "center",
    vertical: "middle",
  },
  border: BORDER,
};

const SUBHEADER_STYLE = {
  fill: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "2ECC71" },
  },
  alignment: {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  },
  border: BORDER,
};

const DATA_ALIGNMENT = {
  horizontal: "center",
  vertical: "middle",
  wrapText: true,
};

const getInternationalData = (international) => {
  if (Array.isArray(international)) {
    return international[0] || null;
  }

  return international && typeof international === "object"
    ? international
    : null;
};

const getDutiesStatus = (shipment) => {
  const internationalData = getInternationalData(shipment?.international);

  if (!internationalData || internationalData?.duties_by !== "shipper") {
    return "-";
  }

  return Number(shipment?.is_duty_paid) === 0 ? "Unpaid" : "Paid";
};

const getShipmentType = (shipmentType) => {
  if (!shipmentType) {
    return "-";
  }

  return shipmentType === "trucker" ? "Carrier" : shipmentType;
};

const getShipmentData = (shipment) => {
  const weightUnit = shipment?.unit === "Imperial" ? "lb" : "kg";

  return {
    "#": shipment?.id ?? "-",
    Client: shipment?.user?.username ?? "-",
    Carrier: shipment?.shipping_company?.shipping_company?.name ?? "-",
    "Shipment Status": shipment?.shipment_status ?? "-",
    Customer: shipment?.tattension ?? "-",
    Weight: `${shipment?.total_weight ?? "-"} ${weightUnit}`,
    "Shipment Type": getShipmentType(shipment?.shipment_type),

    Tracking: {
      "Pickup ID": shipment?.PickupID ?? "-",
      "Bolt Tracking": shipment?.tracking_id ?? "-",
      "Carrier Tracking": shipment?.shipment_tracking_id ?? "-",
      "Package ID": shipment?.parcel_id?.join(", ") ?? "-",
    },

    "Additional Services": {
      "Signature Enabled": shipment?.signature ? "Yes" : "No",
      "Residential Enabled": shipment?.tresidential ? "Yes" : "No",
      "Tailgate Pickup Enabled": shipment?.tailgate_pickup ? "Yes" : "No",
      "Tailgate Delivery Enabled": shipment?.tailgate_delivery ? "Yes" : "No",
    },

    Shipper: {
      Country: shipment?.fcountry ?? "",
      City: shipment?.fcity ?? "",
      State: shipment?.fstate ?? "",
      Zip: shipment?.fzip ?? "",
      Address: shipment?.faddress ?? "",
    },

    Recipient: {
      Country: shipment?.tcountry ?? "",
      City: shipment?.tcity ?? "",
      State: shipment?.tstate ?? "",
      Zip: shipment?.tzip ?? "",
      Address: shipment?.taddress ?? "",
    },

    Billing: {
      "Invoice Status": shipment?.admin_invoice_status ?? "-",
      Billing:
        Number(shipment?.is_bill_verified) === 0 ? "Unverified" : "Verified",
      Duties: getDutiesStatus(shipment),
      "Invoice ID": shipment?.admin_invoice_id ?? "-",
    },

    Payments: {
      "Payment Method": Number(shipment?.by_card) === 1 ? "Card" : "Balance",
      "Markup Percentage": shipment?.markup ?? "-",
      "Markup Value": shipment?.service_charge ?? "-",
      Total: shipment?.Total ?? "-",
      "Created At": shipment?.created_at
        ? convertTime(shipment.created_at)
        : "-",
    },
  };
};

export const adminShipmentExport = async (
  shipments,
  selectedRows,
  setSnack,
) => {
  try {
    const shipmentList = shipments?.data || [];
    const selectedIds = new Set(selectedRows || []);

    const selectedShipments = shipmentList.filter((shipment) =>
      selectedIds.has(shipment?.id),
    );

    if (!selectedShipments.length) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: i18n.t("admin:shipments.selectShipmentsToExport"),
        severity: "error",
      }));

      return;
    }

    const data = selectedShipments.map(getShipmentData);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Shipments");

    const headers = Object.keys(data[0]);

    const subheaders = headers.map((header) => {
      const value = data[0][header];

      return value && typeof value === "object" ? Object.keys(value) : [];
    });

    let columnIndex = 1;

    headers.forEach((header, headerIndex) => {
      const currentSubheaders = subheaders[headerIndex];
      const isGroupedField = currentSubheaders.length > 0;
      const columnSpan = isGroupedField ? currentSubheaders.length : 1;

      // Grouped fields: merge horizontally
      if (isGroupedField && columnSpan > 1) {
        worksheet.mergeCells(1, columnIndex, 1, columnIndex + columnSpan - 1);
      }

      // Single fields: merge vertically across Header + Subheader rows
      if (!isGroupedField) {
        worksheet.mergeCells(1, columnIndex, 2, columnIndex);
      }

      const headerCell = worksheet.getCell(1, columnIndex);

      headerCell.value = header;
      headerCell.style = HEADER_STYLE;

      if (isGroupedField) {
        currentSubheaders.forEach((subheader, index) => {
          const cell = worksheet.getCell(2, columnIndex + index);

          cell.value = subheader;
          cell.style = SUBHEADER_STYLE;

          worksheet.getColumn(columnIndex + index).width = Math.max(
            subheader.length + 4,
            15,
          );
        });
      } else {
        worksheet.getColumn(columnIndex).width = Math.max(
          header.length + 4,
          15,
        );
      }

      columnIndex += columnSpan;
    });

    data.forEach((item, rowIndex) => {
      let currentColumnIndex = 1;

      headers.forEach((header, headerIndex) => {
        const value = item[header];
        const currentSubheaders = subheaders[headerIndex];

        // Grouped fields
        if (currentSubheaders.length > 0) {
          currentSubheaders.forEach((subheader) => {
            const cell = worksheet.getCell(rowIndex + 3, currentColumnIndex);

            cell.value = value?.[subheader] ?? "-";
            cell.alignment = DATA_ALIGNMENT;

            currentColumnIndex += 1;
          });
        }
        // Single fields
        else {
          const cell = worksheet.getCell(rowIndex + 3, currentColumnIndex);

          cell.value = value ?? "-";
          cell.alignment = DATA_ALIGNMENT;

          currentColumnIndex += 1;
        }
      });
    });

    worksheet.views = [
      {
        state: "frozen",
        ySplit: 2,
      },
    ];

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = "shipments.xlsx";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Shipment export failed:", error);

    setSnack((snack) => ({
      ...snack,
      open: true,
      text: i18n.t("admin:shipments.anErrorOccurredWhileGenerating"),
      severity: "error",
    }));
  }
};
